import { useCallback, useEffect, useState } from "react";
import {
  type ConnectedAppsSummary,
  type CutOffConfirmation,
  cutOffAllApps,
  fetchConnectedApps,
} from "../api/apps";
import { AppRequestError } from "../api/client";
import { Confirm } from "../components/Confirm";
import { Screen } from "../components/Screen";
import { ErrorState, Loading } from "../components/states";
import { usePanel } from "../panelContext";
import { CONNECTED_APPS } from "../sections";

/**
 * `/me/apps` — **the apps that can reach Boone on your behalf, and the lever that cuts one
 * off** (issue #116, ADR 0021 decision 9, `ConnectedApps.dc.html` and `RevokeFlow.dc.html`).
 *
 * ## Why this screen is a security fix rather than a feature
 *
 * Dynamic Client Registration is on and it is why we moved to Stytch (ADR 0010): any MCP
 * client can register itself, and once somebody consents at `/authorize` it holds a token that
 * reads and writes their family's shared memory. Until this page there was **no way to see
 * which clients held that grant and no way to take one back** — a compromised Claude account
 * had no lever in the product short of `disband_family`. That is the one-way door #115 closed
 * for membership, still open for hosts.
 *
 * ## Three things the page will not do, each on purpose
 *
 * **It never asserts a name.** Under DCR the name and description are strings the app chose
 * about itself, and in the case this page exists for they are attacker-chosen. So every row
 * reads *Calls itself "Claude"*, with a *registered itself* marker and a footnote saying two
 * apps can claim the same name. `logo_url` is not rendered and is not even on the wire —
 * decision 9 drops it, because it is an attacker-controllable URL that #112's CSP would refuse
 * to load anyway.
 *
 * **It has no "connect an app" button, and the empty state says why.** The grant is created by
 * the OAuth flow the *host* starts; this surface has no part in it. An empty state offering to
 * connect one would send people somewhere it cannot take them.
 *
 * **A cut-off app does not vanish.** It moves to a winding-down row until the window closes.
 * A row that disappeared would read as *gone*, which is false for up to the residual window,
 * on the one screen a worried person refreshes.
 *
 * ## Every sentence about the mechanism is the server's
 *
 * The confirmation's question, what cutting off achieves, the window it cannot close, and the
 * winding-down note all arrive composed (`services/mcp/src/auth/connectedAppCopy.ts`). The
 * residual in particular is a **measured** property of the Stytch project, so a number written
 * into this bundle would go on claiming eight hours the day the project changed. What this
 * file composes is chrome — the page's headings, the *registered itself* marker, and the one
 * thing only a browser can do, which is render an instant in the reader's own zone.
 *
 * ## No step-up in front of the button
 *
 * #307 excludes revocation, and ADR 0021 decision 8a names the exclusion: a second factor here
 * helps an attacker keep the access somebody is trying to cut off. The gate belongs on
 * destruction, not on protection.
 */ export function ConnectedApps() {
  const { api } = usePanel();

  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  /**
   * **The confirmation's own copy, snapshotted — not a boolean over `load`** (PR #364 review).
   *
   * It held a boolean and the dialog rendered from `load.summary`, which `reload()` nulls on
   * its way through `status: "loading"`. So the dialog was torn down by its own re-read: it
   * vanished mid-flight on a partial sweep, which reads as *it worked* — the
   * reassuring-direction lie the server copy refuses to tell, arriving through the render path
   * instead. And when the reload *failed* it never came back, destroying *"2 apps couldn't be
   * cut off"* — the one actionable thing the `failed` count exists to deliver, on the one
   * screen where it matters, in exactly the conditions that produce a partial sweep.
   *
   * Holding the snapshot restores the property the per-app version had for free: the dialog is
   * independent of the list behind it.
   */
  const [confirming, setConfirming] = useState<Asking | null>(null);
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  // `attempt` is not read in the effect — it IS the trigger, the shell's own idiom.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` is the re-run trigger.
  useEffect(() => {
    let live = true;
    setLoad({ status: "loading" });
    fetchConnectedApps(api)
      .then((answer) => {
        if (live)
          setLoad({ status: "ready", summary: answer.summary, note: answer.windingDownNote });
      })
      .catch((error: unknown) => {
        if (live) setLoad({ status: "failed", detail: detailOf(error) });
      });
    return () => {
      live = false;
    };
  }, [api, attempt]);

  /**
   * Cut everything off.
   *
   * **The status is re-read rather than patched**, for the reason the single-app version had:
   * the winding-down instant it comes back with is the server's, derived from the grants' own
   * configured lifetimes, and a line this component assembled from the same numbers would be a
   * second place the window is decided.
   *
   * **A partial sweep keeps the dialog open.** Some grants really did end — the reload behind
   * shows that — but the honest next move is pressing the button again, and closing the dialog
   * on a half-done sweep would put a *"cut off"* status line in front of somebody while apps
   * they asked to disconnect were still connected.
   */
  function confirm(): void {
    if (pending) return;
    setPending(true);
    setRefusal(null);
    cutOffAllApps(api)
      .then((result) => {
        setPending(false);
        reload();
        if (result.failed === 0) {
          setConfirming(null);
          return;
        }
        setRefusal(
          result.failed === 1
            ? "One app couldn't be cut off and is still connected. Try again in a moment."
            : `${result.failed} apps couldn't be cut off and are still connected. Try again in a moment.`,
        );
      })
      .catch((error: unknown) => {
        const failure = cutOffFailure(error, confirming?.count ?? 0);
        setPending(false);
        setRefusal(failure.message);
        // **Re-read when the outcome is unknown.** The copy for that case sends somebody to
        // look at the count, and without this it has not been read since the page loaded.
        if (!failure.outcomeKnown) reload();
      });
  }

  const summary = load.status === "ready" ? load.summary : null;

  return (
    <Screen title={CONNECTED_APPS.label} blurb={CONNECTED_APPS.blurb}>
      {load.status === "loading" ? <Loading label="Looking at what's connected…" /> : null}

      {load.status === "failed" ? (
        <ErrorState title="Couldn't load your connected apps" body={load.detail} />
      ) : null}

      {load.status === "ready" && summary !== null ? (
        <>
          <div className="apps__status">
            <p className="apps__count">
              {summary.connectedCount === 0
                ? summary.nothingConnected
                : summary.connectedCount === 1
                  ? "One app can reach Boone on your behalf."
                  : `${summary.connectedCount} apps can reach Boone on your behalf.`}
            </p>

            {summary.windingDownCount > 0 && summary.windingDownUntil !== null ? (
              <p className="apps__state">
                <span className="apps__dot" aria-hidden="true" />
                {summary.windingDownCount === 1
                  ? "One app was cut off and is winding down until "
                  : `${summary.windingDownCount} apps were cut off and are winding down until `}
                {untilAbout(summary.windingDownUntil)}. {load.note}
              </p>
            ) : null}

            {/*
              **Disabled rather than hidden when nothing is connected.** A control that vanishes
              reads as a feature that is missing; one that is visibly unavailable reads as a
              thing there is currently nothing to do. This page is refreshed by worried people,
              and the second is what they need to see.
            */}
            <button
              className="cta cta--danger apps__cut-all"
              type="button"
              onClick={() => {
                // `null` is what disables the button; the guard keeps the two from drifting.
                if (summary.confirmAll === null) return;
                setConfirming({ copy: summary.confirmAll, count: summary.connectedCount });
                setRefusal(null);
              }}
              disabled={summary.confirmAll === null}
            >
              Cut off every app
            </button>
          </div>

          {/*
            **Why this page does not list them**, said once. It is the whole argument for the
            shape of the screen, so it sits under the lever rather than in the blurb: an app
            registers itself, so the only thing we are ever told is the name it typed — no
            first-seen, no device, nothing that separates one from another. A list of cards a
            reader cannot tell apart is not information, and picking one from it is guesswork.

            **Gated on there being something to cut off** (PR #364 review). It ends by telling
            somebody to cut them all off and reconnect the ones they want, which on an empty
            account is advice about a decision there is nothing to make — under a greyed button,
            below a sentence that already said the whole truth.
          */}
          {summary.connectedCount === 0 ? null : (
            <p className="apps__footnote">
              Boone can't show you which app is which. Each one registers itself and tells us only
              the name it chose — two apps can claim the same name, and reconnecting on a new device
              adds another. So the only honest control is to cut them all off and reconnect the ones
              you still want, in Claude, ChatGPT, or wherever you use Boone.
            </p>
          )}
        </>
      ) : null}

      {confirming === null ? null : (
        <Confirm
          message={confirming.copy.message}
          consequence={confirming.copy.consequence}
          caution={confirming.copy.caution}
          confirmLabel="Cut them off"
          /*
            **"Leave them" stops being true the moment something goes wrong.** It is the right
            label for the question and the wrong one for a dialog that has just said some apps
            are still connected, where pressing it would read as choosing to leave them so.
          */
          cancelLabel={refusal === null ? "Leave them" : "Close"}
          pending={pending}
          error={refusal}
          onConfirm={confirm}
          onCancel={() => {
            setConfirming(null);
            setRefusal(null);
          }}
        />
      )}
    </Screen>
  );
}

/**
 * An open confirmation: the server's sentences, **and how many grants they are about**.
 *
 * The count rides with the copy rather than being read from `load` at the moment of failure,
 * for the same reason the copy does — `reload()` nulls the loaded state, and a sentence that
 * had to reach into it would be back to depending on the read it is reporting the failure of.
 */
interface Asking {
  readonly copy: CutOffConfirmation;
  readonly count: number;
}

type Load =
  | { status: "loading" }
  | { status: "ready"; summary: ConnectedAppsSummary; note: string }
  | { status: "failed"; detail: string };

/**
 * The hour a winding-down row counts toward, in the reader's own zone.
 *
 * **The one piece of this sentence a browser has to compose**, because only it knows the zone
 * — and the reason the instant crosses the wire rather than a formatted string.
 *
 * **Rounded up to the hour**, matching the server's `describeWindow`, and for the same
 * asymmetry: *"about 6 PM"* for a window that actually closes at 6:30 tells somebody the
 * danger has passed half an hour before it has, and there is no corresponding harm in the
 * other direction.
 *
 * A day is named when it is not today, because *"about 2 AM"* on a page opened at 6 PM reads
 * as an hour that has already gone.
 *
 * **The minutes are rendered even though they are always `00`** (PR review). `{ hour:
 * "numeric" }` alone gives *"6 PM"* on a twelve-hour locale and a bare *"18"* on a
 * twenty-four-hour one — a number that does not read as a time at all, and en-GB is a
 * twenty-four-hour locale. `{ hour: "numeric", minute: "2-digit" }` is what `Activity.tsx`
 * already uses, and it costs *"about 6:00 PM"* where the canvas draws *"about 6pm"*: a
 * divergence taken deliberately, because a time that is wrong in the reader's own locale is
 * worse than one that is two characters longer.
 */
export function untilAbout(endsAt: string, now: Date = new Date()): string {
  const at = new Date(endsAt);
  if (Number.isNaN(at.getTime())) return "it expires on its own";

  const rounded = new Date(at);
  if (rounded.getMinutes() > 0 || rounded.getSeconds() > 0 || rounded.getMilliseconds() > 0) {
    rounded.setHours(rounded.getHours() + 1, 0, 0, 0);
  }
  const time = rounded.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

  const days = Math.round((startOfDay(rounded).getTime() - startOfDay(now).getTime()) / 86_400_000);
  if (days <= 0) return `about ${time}`;
  if (days === 1) return `about ${time} tomorrow`;
  return `about ${time} on ${rounded.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  })}`;
}

function startOfDay(at: Date): Date {
  const day = new Date(at);
  day.setHours(0, 0, 0, 0);
  return day;
}

/**
 * What to show when a request did not produce a list.
 *
 * **The server's own sentence where there is one**, which is both refusals this screen can
 * meet: an unreachable Stytch (*"Nothing has changed — try again in a moment"*) and an app
 * that is no longer connected. The first is the one that matters — ADR 0021 names taking on
 * Stytch's availability for this screen as a consequence of building it, and *"nothing has
 * changed"* is the only useful thing to say about it, which a generic line could not.
 */
function detailOf(error: unknown): string {
  if (error instanceof AppRequestError && error.detail !== null) return error.detail;
  return "Something went wrong reading your connected apps. Try again in a moment — nothing has changed.";
}

/**
 * What to show when a cut-off did not come back cleanly — **and whether we know what happened
 * to the grant** (PR review, two rounds).
 *
 * **It may not say "nothing has changed".** That claim is safe about a failed *read* and is
 * exactly what this screen must never assert about a failed *write*: the request may have
 * reached Stytch and revoked the grant before whatever went wrong went wrong. Telling somebody
 * nothing happened would leave them believing an app still had access when it did not — or,
 * worse on this page, believing it still had access when they had just taken it away and
 * stopped watching.
 *
 * **`outcomeKnown` is decided by the server's `code`, never by whether a message came with
 * it** (PR #363 review). It used to be *"any refusal carrying a `detail` knows what
 * happened"*, on the reasoning that every refusal this route produces is raised before or
 * instead of the revoke. That reasoning was wrong about one of them: `apps_unavailable` was
 * also raised when the **revoke call itself** failed, and a `fetch` that throws cannot tell a
 * request that never landed from one whose answer was lost. So the one case that genuinely
 * could not say carried a sentence claiming nothing had changed, and — because it had a
 * `detail` — suppressed the re-read added for exactly it.
 *
 * The server now distinguishes them, and this reads the distinction rather than inferring it:
 *
 * - `not_connected` — never reached Stytch. Untouched.
 * - `apps_unavailable` — the listing before the revoke failed, or the revoke failed and a
 *   re-read *proved* the grant survived. Untouched, and now that is knowledge rather than an
 *   assumption.
 * - `cut_off_failed` — the revoke did not land, confirmed by re-reading. Untouched.
 * - anything else, including `cut_off_outcome_unknown` and a 500 — nothing can say.
 *
 * The unknown case says what is actually known, which is nothing, and sends them to the one
 * thing that can answer — honest only because {@link ConnectedApps} re-reads the list on
 * exactly this branch. *"If it's still there, it's still connected"* is true of a freshly read
 * list and false of a stale one.
 */
interface CutOffFailure {
  readonly message: string;
  /** True when the server told us the grant is untouched. False when nothing can say. */
  readonly outcomeKnown: boolean;
}

/**
 * The codes whose refusal *proves* the grant survived.
 *
 * An allowlist rather than a list of the ambiguous ones, for the reason
 * `control-writes-go-through-tools.test.ts` gives about its own: a blocklist catches what
 * somebody already thought of, and the code that matters is the one added next year. A new
 * refusal is treated as unknown until it is deliberately added here — which fails safe, by
 * re-reading and declining to claim anything.
 */
const OUTCOME_KNOWN_CODES: ReadonlySet<string> = new Set([
  "not_connected",
  "apps_unavailable",
  "cut_off_failed",
]);

function cutOffFailure(error: unknown, count: number): CutOffFailure {
  if (error instanceof AppRequestError && error.detail !== null) {
    return { message: error.detail, outcomeKnown: OUTCOME_KNOWN_CODES.has(error.code) };
  }
  return { message: outcomeUnknownFallback(count), outcomeKnown: false };
}

/**
 * **The sentence for a failure that arrived with no sentence of its own** (PR #364 review).
 *
 * It said *"whether **that app** was cut off. **Close this and look at the list**"* — right on
 * `main`, where there was a list and a per-app dialog to close, and wrong the moment this
 * screen replaced both. Singular over N grants, and pointing at a surface that no longer
 * exists, on the one path where nothing can say what happened.
 *
 * **It is reached on two real paths, not a theoretical one.** `client.ts` gives `detail` as
 * `null` for a refusal carrying no message *and* for `UNREACHABLE` — so an offline browser or
 * a DNS failure on the sweep lands here, which is the likeliest failure of the lot. And
 * `cutOffAllConnectedApps` rethrows a fault of ours when nothing landed, which
 * `connectedAppRefusal` declines to map, so the router answers `500 { error:
 * "internal_error" }` with no message at all.
 *
 * **It deliberately mirrors the server's `cutOffOutcomeUnknownMessage`.** Two sentences saying
 * the same thing in two places is a seam — but the alternative is worse: this one exists
 * *because* nothing usable came back from the server, so it cannot be composed there. What
 * belongs here is a count, which the browser knows; what stays the server's is the residual
 * window, which it must never derive. `tests/connectedApps.test.tsx` asserts both.
 */
function outcomeUnknownFallback(count: number): string {
  const subject = count === 1 ? "that app was" : "those apps were";
  return (
    `Something went wrong, and Boone can't tell whether ${subject} cut off. ` +
    "Reload this page — the count at the top is what can still reach Boone."
  );
}
